import React, { FC, useCallback } from 'react'
import { Field, Formik, FormikProps, ErrorMessage } from 'formik'
import { Trans, t } from '@lingui/macro'
import { useAllGroupsQuery } from 'front-end/src/graphql/commonQueries/allGroups.codegen'
import {
  ButtonClean,
  ButtonPrimary,
  Modal,
  Checkbox
} from 'front-end/src/components'
import Form, { FormRow } from '../../../../components/common/form/Form'
import Label from '../../../../components/common/form/label/Label'
import Input from '../../../../components/common/form/input/Input'
import { notification } from '../../../../components/common/notifications/Notifications'
import { OrganisationRoleEnum } from 'types/Organisation'
import { useManageUsersPageContext } from '../../ManageUsersPageContext'
import { useCreateUserMutation } from './operations/createUserMutation'
import { ICreateUserInput } from './types/CreateUserInput'
import {
  initialCreateUserData,
  initialCreateAccountNameData,
  CreateUserSchema,
  CreateUserSchemaAccountName
} from './CreateUserFormData'
import './CreateUserModal.less'
import { useFeatureFlags } from 'front-end/src/app/hooks/useFeatureFlags'

interface IModalFormProps extends FormikProps<ICreateUserInput> {
  show: boolean
  onHide: () => void
}

const ModalForm: FC<IModalFormProps> = ({
  resetForm,
  handleSubmit,
  isSubmitting,
  values,
  setFieldValue,
  onHide,
  show,
  submitForm
}) => {
  const { featureFlags } = useFeatureFlags()
  const handleHide = useCallback(() => {
    resetForm()
    onHide()
  }, [resetForm, onHide])

  const allGroupsData = useAllGroupsQuery()
  const groups = allGroupsData.data?.groups

  return (
    <Modal
      className="CreateUserModal"
      header={t`Add new user`}
      show={show}
      onHide={handleHide}
      controls={[
        <ButtonClean key={1} onClick={handleHide}>
          <Trans>Close</Trans>
        </ButtonClean>,
        <ButtonPrimary key={2} onClick={submitForm} disabled={isSubmitting}>
          <Trans>Create user</Trans>
        </ButtonPrimary>
      ]}
    >
      <Form onSubmit={handleSubmit}>
        {featureFlags?.account_name_login && (
          <FormRow>
            <Label>
              <Trans>Account name</Trans>
            </Label>
            <Field
              fullWidth
              placeholder={t`Account name`}
              name="account_name"
              autoComplete="off"
              component={Input}
            />
            <div className="ErrorWrap">
              <ErrorMessage name="account_name" />
            </div>
          </FormRow>
        )}
        <FormRow>
          <Label>
            <Trans>Email</Trans>
          </Label>
          <Field
            fullWidth
            placeholder="user@example.com"
            name="email"
            autoComplete="off"
            component={Input}
          />
          <div className="ErrorWrap">
            <ErrorMessage name="email" />
          </div>
        </FormRow>
        <FormRow className="flex-row">
          <div className="form-control">
            <Label>
              <Trans>First name</Trans>
            </Label>
            <Field
              fullWidth
              placeholder="John"
              name="first_name"
              autoComplete="off"
              component={Input}
            />
            <div className="ErrorWrap">
              <ErrorMessage name="first_name" />
            </div>
          </div>
          <div className="form-control">
            <Label>
              <Trans>Last name</Trans>
            </Label>
            <Field
              fullWidth
              placeholder="Smith"
              name="last_name"
              autoComplete="off"
              component={Input}
            />
            <div className="ErrorWrap">
              <ErrorMessage name="last_name" />
            </div>
          </div>
        </FormRow>
        <FormRow>
          <Label>
            <Trans>Role</Trans>
          </Label>
          <Field
            fullWidth
            placeholder={t`PR Manager`}
            name="position"
            autoComplete="off"
            component={Input}
          />
        </FormRow>
        {groups && (
          <FormRow>
            <Label>
              <Trans>Group</Trans>
            </Label>
            <div className="FormSelect Select--filter group">
              <Field name="groupId" component="select">
                <option value="" key={0}>
                  {t`None`}
                </option>
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </Field>
            </div>
          </FormRow>
        )}
        <FormRow>
          <Checkbox
            isChecked={values.organisation_role === OrganisationRoleEnum.ADMIN}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.checked
                ? OrganisationRoleEnum.ADMIN
                : OrganisationRoleEnum.STUDENT
              setFieldValue('organisation_role', value)
            }}
          >
            <Trans>Change role to Full administrator</Trans>
          </Checkbox>
          {values.organisation_role === OrganisationRoleEnum.ADMIN && (
            <div className="CreateUserModal__adminInfo">
              <Trans>
                Account administrators have the ability to edit and view all
                content on your account, add and remove users and view all
                reports
              </Trans>
            </div>
          )}
        </FormRow>
      </Form>
    </Modal>
  )
}

export const CreateUserModal: FC<{
  show: boolean
  onHide: () => void
}> = ({ show, onHide }) => {
  const { featureFlags } = useFeatureFlags()
  const [createUserMutationFn] = useCreateUserMutation()
  const { fetchUsers } = useManageUsersPageContext()

  const onSubmit = useCallback(
    async (user, actions) => {
      let userInput = user
      if (!userInput.email && !userInput.account_name) {
        notification.error(t`You must fill email or account name.`)
        return
      }

      if (!userInput.email) {
        delete userInput.email
      }
      if (userInput.account_name === '') {
        delete userInput.account_name
      }

      if (!userInput.groupId) {
        userInput.groupId = null
      }

      try {
        await createUserMutationFn({
          variables: { user: userInput as any }
        })
        notification.info(t`User created`)
        actions.resetForm()
        onHide()

        fetchUsers && fetchUsers()
      } catch (err) {
        console.error(err)
      } finally {
        actions.setSubmitting(false)
      }
    },
    [createUserMutationFn]
  )

  return (
    <Formik<ICreateUserInput>
      onSubmit={onSubmit}
      initialValues={
        featureFlags?.account_name_login
          ? initialCreateAccountNameData()
          : initialCreateUserData()
      }
      validationSchema={
        featureFlags?.account_name_login
          ? CreateUserSchemaAccountName
          : CreateUserSchema
      }
    >
      {(props) => <ModalForm show={show} onHide={onHide} {...props} />}
    </Formik>
  )
}
